import { DictionaryWord, WordStatusServer } from 'app/types';

/**
 * 4-state lifecycle (mirrors BE `WordStatus` enum). The legacy 2-boolean
 * (isLearned + isActiveSpeech) model lives on the entity for backward
 * compatibility but the FE consumes only this enum from the v2 page onward.
 */
export type WordStatus = WordStatusServer;

export interface VocabStatusMeta {
  /** Single-line label shown on chips / status cells. */
  label: string;
  /** Light-mode tint. */
  color: string;
  /** Dark-mode tint — softer to avoid blowing out on dark surfaces. */
  dark: string;
  /** Hint shown in the settings popover and legend. */
  desc: string;
}

export const VOCAB_STATUSES: Record<WordStatus, VocabStatusMeta> = {
  NEW: {
    label: 'Нове',
    color: '#3B2C93',
    dark: '#9B7CFF',
    desc: 'ще не вчилось',
  },
  REPEAT: {
    label: 'Повторити',
    color: '#777BFF',
    dark: '#9FA3FF',
    desc: 'потребує освіження',
  },
  LEARNED: {
    label: 'Вивчено',
    color: '#5FA56F',
    dark: '#74C98A',
    desc: 'слово засвоєне',
  },
  ACTIVE: {
    label: 'Активне мовлення',
    color: '#27CCBC',
    dark: '#3FE0CE',
    desc: 'вживаєш у власному мовленні',
  },
};

/**
 * Sort order for chip rows, picker menus, legends. Goes left→right from
 * most-engaged to least-engaged: ACTIVE → LEARNED → REPEAT → NEW. Reads
 * top-down as "what the user is doing now → what's still pending."
 */
export const STATUS_ORDER: WordStatus[] = ['ACTIVE', 'LEARNED', 'REPEAT', 'NEW'];

/**
 * Derive a 4-state status from the BE payload. Prefers the new `status`
 * field; falls back to the legacy booleans for non-migrated endpoints.
 *
 *   isActiveSpeech → ACTIVE
 *   isLearned only → LEARNED
 *   neither        → NEW
 *
 * `REPEAT` only comes from the server — there's no way to derive it from the
 * two-boolean model. Such words round-trip as LEARNED in legacy payloads.
 */
export const statusOf = (
  word: Pick<
    DictionaryWord,
    'isLearned' | 'isActiveSpeech' | 'status'
  >,
): WordStatus => {
  if (word.status) return word.status;
  if (word.isActiveSpeech) return 'ACTIVE';
  if (word.isLearned) return 'LEARNED';
  return 'NEW';
};

export const statusColor = (key: WordStatus, mode: 'light' | 'dark') => {
  const m = VOCAB_STATUSES[key];
  return mode === 'dark' ? m.dark : m.color;
};

/** Status chip-filter value: `all` or any of the four lifecycle states. */
export type StatusFilterValue = 'all' | WordStatus;

export const STATUS_FILTER_CHIPS: Array<{
  value: StatusFilterValue;
  label: string;
  status?: WordStatus;
}> = [
  { value: 'all', label: 'Усі' },
  { value: 'ACTIVE', label: 'Активне', status: 'ACTIVE' },
  { value: 'LEARNED', label: 'Вивчено', status: 'LEARNED' },
  { value: 'REPEAT', label: 'Повторити', status: 'REPEAT' },
  { value: 'NEW', label: 'Нові', status: 'NEW' },
];

const POS_LABELS: Record<string, string> = {
  NOUN: 'Іменники',
  VERB: 'Дієслова',
  ADJECTIVE: 'Прикметники',
  ADVERB: 'Прислівники',
  PREPOSITION: 'Прийменники',
  CONJUNCTION: 'Сполучники',
  PRONOUN: 'Займенники',
  INTERJECTION: 'Вигуки',
  ARTICLE: 'Артиклі',
  NUMERAL: 'Числівники',
};

const POS_COLORS: Record<string, string> = {
  NOUN: '#777BFF',
  VERB: '#27CCBC',
  ADJECTIVE: '#9B7CFF',
  ADVERB: '#F2B14C',
  PREPOSITION: '#FF7043',
  CONJUNCTION: '#5FA56F',
  PRONOUN: '#3B2C93',
  INTERJECTION: '#FFC107',
  ARTICLE: '#5FA56F',
  NUMERAL: '#777BFF',
};

const POS_DEFAULT_COLOR = '#777BFF';

export const posLabel = (pos?: string | null): string => {
  if (!pos) return '—';
  const key = pos.toUpperCase();
  return POS_LABELS[key] ?? pos.toLowerCase();
};

export const posShortLabel = (pos?: string | null): string => {
  if (!pos) return '—';
  return pos.toLowerCase();
};

export const posColor = (pos?: string | null): string => {
  if (!pos) return POS_DEFAULT_COLOR;
  return POS_COLORS[pos.toUpperCase()] ?? POS_DEFAULT_COLOR;
};

export const fmtN = (n: number) => n.toLocaleString('uk-UA');

export const slivWord = (n: number) => {
  const d = n % 10;
  const h = n % 100;
  if (d === 1 && h !== 11) return 'слово';
  if (d >= 2 && d <= 4 && (h < 12 || h > 14)) return 'слова';
  return 'слів';
};

export const fmtAddedDate = (iso?: string | null) => {
  if (!iso) return '—';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '—';
  const dd = String(dt.getDate()).padStart(2, '0');
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const yyyy = dt.getFullYear();
  const hh = String(dt.getHours()).padStart(2, '0');
  const min = String(dt.getMinutes()).padStart(2, '0');
  return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
};

export const hexToRgba = (hex: string, a: number) => {
  const h = hex.replace('#', '');
  return `rgba(${parseInt(h.slice(0, 2), 16)}, ${parseInt(
    h.slice(2, 4),
    16,
  )}, ${parseInt(h.slice(4, 6), 16)}, ${a})`;
};

/** UA-language column labels for the v2 settings popover. */
export const VOCAB_COLUMN_LABELS_UA: Record<string, string> = {
  number: '№',
  word: 'Слово',
  transcription: 'Транскрипція',
  translation: 'Переклад',
  partOfSpeech: 'Частина мови',
  definition: 'Визначення',
  status: 'Статус',
  addedAt: 'Додано',
};
